import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import ReactQuillEditor from "../../../components/ReactQuillEditor";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  InputNumber,
  Row,
  Col,
  Badge,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  FileDoneOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const AddTermsConditions = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);

  const fetchLanguages = async () => {
    const response = await masterService.getVideoLanguages({
      isAll: true,
      status: "Y",
    });
    const data = response?.data?.data || response?.data || [];
    const list = data.map((item) => ({
      value: item.lang_id,
      label: item.lang_name,
    }));
    setLanguages(list);
  };

  useEffect(() => {
    setPageTitle("Add Terms & Conditions");
    fetchLanguages();
  }, [setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await masterService.addTermsConditions(values);

      if (response.data && response.data.status) {
        notify.success({
          message:
            response.data.message || "Terms conditions added successfully",
        });
        form.resetFields();
        navigate("/terms-conditions");
      } else {
        notify.error({
          message: response.data.message || "Failed to add terms conditions",
        });
      }
    } catch (error) {
      console.error("Error adding terms conditions:", error);
      notify.error({
        message: error?.data?.message || "Error adding terms conditions",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  const handleGoBack = () => {
    navigate("/terms-conditions");
  };

  return (
    <div className="min-h-screen">
      <Card
      >
        <Form
          form={form}
          layout="vertical"
          size="large"
          onFinish={handleSubmit}
          initialValues={{
            is_active: "Y",
          }}
          className="space-y-6"
        >
          {/* Terms & Conditions Information Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <FileDoneOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Terms & Conditions Information
              </Title>
            </div>

            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Order
                    </span>
                  }
                  name="tc_order"
                  rules={[
                    {
                      required: true,
                      message: "Please enter terms conditions order",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Enter terms conditions order"
                    style={{ width: "100%" }}
                    min={1}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Status
                    </span>
                  }
                  name="is_active"
                >
                  <Select className="rounded-lg">
                    <Option value="Y">
                      <Badge status="success" text="Active" />
                    </Option>
                    <Option value="N">
                      <Badge status="default" text="Inactive" />
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Heading
                    </span>
                  }
                  name="tc_heading"
                  rules={[{ required: true, message: "Please enter heading" }]}
                >
                  <Input
                    placeholder="Enter heading"
                    maxLength={255}
                    showCount
                    className="rounded-lg"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 16]}>
              <Col span={24}>
                <Form.Item
                  label={
                    <span
                      className="font-semibold"
                      style={{ color: "rgba(0, 0, 0, 0.88)" }}
                    >
                      Text
                    </span>
                  }
                  name="tc_text"
                  rules={[{ required: true, message: "Please enter text" }]}
                >
                  {/* <TextArea
                    placeholder="Enter text"
                    rows={6}
                    showCount
                    className="rounded-lg"
                  /> */}
                  <ReactQuillEditor
                      placeholder="Enter text"
                      Height={400}
                    />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Multilingual Data Section */}
          <div
            className="bg-white p-6 rounded-lg border border-gray-200"
            style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
          >
            <div className="flex items-center gap-2 mb-4">
              <FileDoneOutlined />
              <Title
                level={4}
                className="!mb-0"
                style={{ color: "rgba(0, 0, 0, 0.88)" }}
              >
                Multilingual Data
              </Title>
            </div>

            <Form.List name="multilingual_data">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="mb-4 p-4 rounded-lg border">
                      <div className="flex justify-between items-center mb-4">
                        <span
                          className="font-semibold"
                          style={{ color: "rgba(0, 0, 0, 0.88)" }}
                        >
                          Language Entry #{index + 1}
                        </span>
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => remove(name)}
                          icon={<DeleteOutlined />}
                          style={{ fontWeight: 600 }}
                        >
                          Remove
                        </Button>
                      </div>

                      <div className="grid grid-cols-[200px_1fr] gap-4">
                        <Form.Item
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Language
                            </span>
                          }
                          name={[name, "lang_id"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select language",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select language"
                            options={languages}
                            className="rounded-lg"
                            showSearch
                            optionFilterProp="label"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "tc_heading"]}
                          label={
                            <span
                              className="font-semibold"
                              style={{ color: "rgba(0, 0, 0, 0.88)" }}
                            >
                              Heading
                            </span>
                          }
                          rules={[
                            {
                              required: true,
                              message: "Please enter heading",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter heading in this language"
                            maxLength={255}
                            showCount
                            className="rounded-lg"
                          />
                        </Form.Item>
                      </div>

                      <Form.Item
                        {...restField}
                        name={[name, "tc_text"]}
                        label={
                          <span
                            className="font-semibold"
                            style={{ color: "rgba(0, 0, 0, 0.88)" }}
                          >
                            Text
                          </span>
                        }
                        rules={[
                          {
                            required: true,
                            message: "Please enter text",
                          },
                        ]}
                      >
                        {/* <TextArea
                          placeholder="Enter text in this language"
                          rows={4}
                          showCount
                          className="rounded-lg"
                        /> */}
                        <ReactQuillEditor
                      placeholder="Enter text in this language"
                      Height={400}
                    />
                      </Form.Item>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ tc_heading: "", tc_text: "" })}
                    className="w-full rounded-lg"
                    size="large"
                  >
                    Add Language
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          {/* Action Buttons */}
          <div
            className="flex justify-end gap-4 pt-6 border-t"
            style={{ borderColor: "#d9d9d9" }}
          >
            <Space size="middle">
              <Button
                onClick={handleReset}
                icon={<ReloadOutlined />}
                size="large"
                className="px-6"
              >
                Reset
              </Button>
              <Button
                onClick={handleGoBack}
                icon={<ArrowLeftOutlined />}
                size="large"
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                size="large"
                className="px-8"
              >
                {loading ? "Adding..." : "Add Terms Condition"}
              </Button>
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default AddTermsConditions;
